import React, { useEffect, useState } from "react"
import { Button, Col, Modal } from "react-bootstrap"
import { injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import ActionButton from "../../common/button/actionbutton.component"
import Checkbox from "../../common/checkbox/checkbox.component.jsx"
import StandardList from "../../common/listing/list.component"
import { InventoryAdjustmentModalProps } from "../../common/types/inventory"
import InputUnitOfMeasure from "../../common/unit-of-measure/input.component"
import { InventoryStockDocumentAction } from "../../state/inventory"
import { StockDocumentSelector } from "../../state/inventory/stockdocument/selectors"
import { InventoryAdjustmentRow } from "../../state/inventory/stockdocument/services"
import { ItemMovementRow } from "../../state/pricing/PricingRow"
import { PricingTableSelector, getTableId } from "../../state/pricing/selectors"
import { INVENTORY_STOCK_DIRECTION, STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES } from "../../utils/inventory"


export const InventoryAdjustmentModal = (props:InventoryAdjustmentModalProps) => {
    const dispatch = useDispatch()
    const pricingTableItems = useSelector(PricingTableSelector.getPricingTableItems) 
    const tableId = useSelector(getTableId)
    const documentDirection = useSelector(StockDocumentSelector.getDirection)
    const adjustmentIsLoading = useSelector(StockDocumentSelector.getAdjustmentIsLoading)
    const [items, setItems] = useState<ItemMovementRow[]>(pricingTableItems as ItemMovementRow[])
    const [itemsQtyChecked, setItemsQtyChecked] = useState<number>(0)
      
    const setItemsMeasure = (value: string | undefined) => {
        return items.map(r => {
            if (props.direction === INVENTORY_STOCK_DIRECTION.OUT) {
                const pendingSent = Number(r.attributes.measure) - Number(r.attributes.inventorySentMeasure || 0) || 0
                r.attributes.inventoryToSendMeasure = value || pendingSent.toString()
            }
            else if (props.direction === INVENTORY_STOCK_DIRECTION.IN){
                const pendingReceive = Number(r.attributes.measure) - Number(r.attributes.inventoryReceivedMeasure || 0) || 0
                r.attributes.inventoryToReceiveMeasure=value || pendingReceive.toString()
            }
            return r
        })
    }
    
    useEffect(() => {
        //filter by + or - for IN/OUT
        const newItems=setItemsMeasure("0")
        setItems(newItems)
    }, [])

    const onClickReceive = () => {
        const receiveItems = items.filter(i=>Number(i.attributes.inventoryToReceiveMeasure)>0).map(i => {
            return { "rowId": i.id, "measure": i.attributes.inventoryToReceiveMeasure }
        }) as InventoryAdjustmentRow[]

        dispatch(InventoryStockDocumentAction.receiveItems({
            adjustmentType: props.direction,
            tableId: tableId,
            rows: receiveItems
        }))

        props.onCloseCallback()
    }

    const onClickSend = () => {
        const sendItems = items.filter(i=>Number(i.attributes.inventoryToSendMeasure)>0).map(i => {
            return { "rowId": i.id, "measure": i.attributes.inventoryToSendMeasure }
        }) as InventoryAdjustmentRow[]

        dispatch(InventoryStockDocumentAction.sendItems({
            adjustmentType: props.direction,
            tableId: tableId,
            rows: sendItems
        }))

        props.onCloseCallback()
    }

    const onCheckAll = (isChecked) => {
        items.map((item) => {
            if (!item.attributes.disabled) {
                onItemChecked(item, isChecked)
            }
            return item
        })
        checkStateHandler()
    }

    const onItemChecked = (item, isChecked) => {
        const updatedItems = items.map((originalItem) => {
            if (originalItem.id === item.id) {
                const updatedItem = { ...originalItem } 
                updatedItem.attributes.checked = isChecked 
                
                if (props.direction === INVENTORY_STOCK_DIRECTION.IN) {
                    const pendingReceive = Number(originalItem.attributes.measure)-
                    Number(originalItem.attributes.inventoryReceivedMeasure || 0) || 0 
                    updatedItem.attributes.inventoryToReceiveMeasure = isChecked ? pendingReceive.toString() : "0" 
                }
                
                if (props.direction === INVENTORY_STOCK_DIRECTION.OUT) {
                    const pendingSent = Number(originalItem.attributes.measure) - Number(originalItem.attributes.inventorySentMeasure || 0) || 0 
                    updatedItem.attributes.inventoryToSendMeasure = isChecked ? pendingSent.toString() : "0" 
                }
                
                return updatedItem 
            }
            return originalItem 
        }) 
    
        setItems(updatedItems) 
        checkStateHandler() 
    }
    

    const checkStateHandler = () => {
        setItemsQtyChecked(items.filter(item => item.attributes.checked).length)
    }

    const isAllCheck = () => {
        return itemsQtyChecked == items.length
    }

    const isChecked = () => {
        return itemsQtyChecked > 0
    }
   
    const sendHeaders = () => {
        return [
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.item),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantityToSend),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantitySent),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantityToAdjusted),
            <Checkbox id={"check-all"} key={"check-all"}
                checked={isChecked()}
                isPartiallyChecked={!isAllCheck()}
                onChange={isChecked => onCheckAll(isChecked)}/>
        ]
        
    }

    const receiveHeaders = () => {
        return [
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.item),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantityToReceive),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantityReceived),
            props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.quantityToAdjusted),
            <Checkbox id={"check-all"} key={"check-all"}
                checked={isChecked()}
                isPartiallyChecked={!isAllCheck()}
                onChange={isChecked => onCheckAll(isChecked)}/> 
        ]
    }

    const getModalHeader = () => {
        if (props.direction==INVENTORY_STOCK_DIRECTION.OUT){
            return props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.sendItems)
        }
        else{
            return props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.receiveItems)
        } 
    }

    const getModalButtonText = () => {
        if (props.direction == INVENTORY_STOCK_DIRECTION.IN){
            return props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.receiveItems)
        }else{
            return props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.sendItems)
        } 
    }


    const getHeaders = () =>{
        if (props.direction == INVENTORY_STOCK_DIRECTION.OUT){
            return sendHeaders()
        }else{
            return receiveHeaders()
        }
    }

    
    const handleOnChangeQuantity = (row: ItemMovementRow, value) => {
        let pending = 0
        if (props.direction == INVENTORY_STOCK_DIRECTION.IN){
            pending = Number(row.attributes.measure) - Number(row.attributes.inventoryReceivedMeasure) || 0
            if (pending >= value && value >=0) {
                row.attributes.inventoryToReceiveMeasure = value
            }
        } else {
            pending= Number(row.attributes.measure) - Number(row.attributes.inventorySentMeasure) || 0
            if (pending >= value && value>=0) {
                row.attributes.inventoryToSendMeasure = value  
            }
        }
            
        const newItems=items.filter(r=>r.id!=row.id)
        setItems([...newItems,row])
    }

    const mapRowReceive = (row: ItemMovementRow) => {
        const pending = Number(row.attributes.measure) - Number(row.attributes.inventoryReceivedMeasure || 0) || 0
        return {
            rowId: row.id,
            data: [
                row.attributes.title,
                pending,
                row.attributes.inventoryReceivedMeasure || "0",
                <div key={row.id}>
                    <InputUnitOfMeasure
                        disabled={pending==0}
                        debounced={true}
                        value={row.attributes.inventoryToReceiveMeasure || "0"}
                        unit={row.attributes.unitOfMeasure}
                        onChange={val => handleOnChangeQuantity(row, val)}
                        format={"short"}
                    />
                </div>,
                <Checkbox id={"received-item-" + row.id}
                    disabled ={pending===0}
                    key = {row.id}
                    checked={row.attributes.checked}
                    onChange={isChecked => onItemChecked(row, isChecked)}
                    style={{ top: 0, display: pending === 0 ? "none" : "block" }}
                />
            ]
        }
    }

    const mapRowSent = (row: ItemMovementRow) => {
        const pending = Number(row.attributes.measure) - Number(row.attributes.inventorySentMeasure || 0) || 0
        return {
            rowId: row.id,
            data: [
                row.attributes.title,
                pending,
                row.attributes.inventorySentMeasure || "0",
                <div key={row.id}>
                    <InputUnitOfMeasure
                        disabled={pending==0}
                        debounced={true}
                        value={row.attributes.inventoryToSendMeasure || "0"}
                        unit={row.attributes.unitOfMeasure}
                        onChange={(val)=>handleOnChangeQuantity(row,val)}
                        format={"short"}
                    />
                </div>,
                <Checkbox id={"sent-item-" + row.id}
                    disabled ={pending===0}
                    key = {row.id}
                    checked={row.attributes.checked}
                    onChange={isChecked => onItemChecked(row, isChecked)}
                    style={{ top: 0, display: pending === 0 ? "none" : "block" }}
                />
            ]
        }
            
    }

    const getRows = () => {
        let pricingElements=pricingTableItems
        if (props.direction == INVENTORY_STOCK_DIRECTION.OUT) {
            if (documentDirection == INVENTORY_STOCK_DIRECTION.IN_OUT) {
                pricingElements=pricingTableItems.filter(r=>Number(r.attributes.measure)<0)
            }
            return pricingElements.map((x) => mapRowSent(x as ItemMovementRow))
        }

        if (documentDirection == INVENTORY_STOCK_DIRECTION.IN_OUT) {
            pricingElements=pricingTableItems.filter(r=>Number(r.attributes.measure)>=0)
        }
        return pricingElements.map((x) => mapRowReceive(x as ItemMovementRow))
    }

    return(
        <Modal
            id={"product-movement-modal"}
            show={true}
            onHide={props.onCloseCallback}
            scrollable={true}
            backdrop="static"
            size="lg"
            centered={true}>
                
            <Modal.Header closeButton>
                <Modal.Title>
                    {getModalHeader()}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Col>
                    <StandardList
                        className="row pt-3"
                        headers={getHeaders()}
                        rows={getRows()}
                        smallLine={true}
                    />
                </Col>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={props.onCloseCallback}
                    variant="secondary">
                    {props.intl.formatMessage(STOCK_DOCUMENT_MOVEMENT_MODAL_MESSAGES.close)}
                </Button>
                {props.direction == INVENTORY_STOCK_DIRECTION.IN &&
                    <ActionButton
                        loadingTimeout={30000}
                        id={"card-button"}
                        className="btn btn-primary"
                        isLoading={adjustmentIsLoading}
                        disabled={items.filter(i => i.attributes.inventoryToReceiveMeasure &&
                            Number(i.attributes.inventoryToReceiveMeasure) != 0).length == 0}
                        onClick={() => {
                            onClickReceive()}}
                    >
                        {getModalButtonText()}
                    </ActionButton> 
                }
                {props.direction == INVENTORY_STOCK_DIRECTION.OUT &&
                     <ActionButton
                         loadingTimeout={30000}
                         id={"card-button"}
                         className="btn btn-primary justify-content-end"
                         isLoading={adjustmentIsLoading}
                         disabled={items.filter(i => i.attributes.inventoryToSendMeasure &&
                        Number(i.attributes.inventoryToSendMeasure) != 0).length == 0}
                         onClick={() => {
                             onClickSend()}}
                     >
                         {getModalButtonText()}
                     </ActionButton>
                }
            </Modal.Footer>
        </Modal>)    

}

export default injectIntl(InventoryAdjustmentModal)


// WEBPACK FOOTER //
// ./src/inventory/modals/InventoryAdjustmentModal.tsx